/**
 * findBlockingInstance — resolve WHICH running instance the substrate's
 * same-email concurrent-run guard (`start_workflow` step 6b,
 * `workflows/tools/start_workflow.py::_concurrent_run_guard`) is blocking a
 * launch on, after that launch has already come back `conflict` (HTTP 409).
 *
 * ONE EXCEPTION to `core/`'s usual "extracted verbatim from the add-in,
 * frozen once landed" rule: this module is ADDITIVE, copied verbatim from
 * the add-in's own `ui/apps/outlook-addin/src/services/findRunningInstanceForEmail.ts`
 * (the pure matcher: `matchRunningInstance` / `instanceEmailAnchors` /
 * `candidateIds`) and `ui/apps/outlook-addin/src/types/workflow.ts`
 * (`IN_PROGRESS_STATUSES` / `LAUNCH_BLOCKING_STATUSES`) — the add-in still
 * has its OWN copy today and keeps calling it; Phase 3 deletes the add-in's
 * copy and switches it to import this one. Until then the two must never
 * diverge (same requirement `prefillInputs.ts` documents for its own
 * temporarily-duplicated copy).
 *
 * VERIFIED 2026-09-21 (workflows-substrate investigation): the 409 body
 * ora's `action` forwards carries NO reliable blocking-instance-id — the
 * .NET bridge (`GlobalExceptionHandler.cs`) builds the `ProblemDetails` from
 * only `message`/`trace_id`, dropping the substrate's `extras` dict where
 * `instance_id` actually lives (`start_workflow.py`'s own comment: "the
 * product-api bridge forwards code/message/trace_id and DROPS the extras
 * dict"). That is exactly why the add-in never parses the 409 body for this
 * — it makes this SEPARATE, best-effort lookup instead
 * (`findBlockingInstanceForEmail`'s own docstring: "this lookup only puts a
 * name and a status on it"). Ora's host wrapper (`oraLaunchIO.findBlockingInstance`,
 * backed by the triage route's `find-blocking` action intent →
 * `service.listMyInstances`) mirrors that shape exactly.
 *
 * Row shape is a MINIMAL structural type both hosts' real instance DTOs
 * satisfy: ora's `SubstrateInstanceItem` (`workflows.service.server.tsx`)
 * and the add-in's `ActiveWorkflow` (`types/workflow.ts`) both declare
 * `instance_id` (never `id`) — `id` is accepted too, defensively, exactly as
 * asked, so a future/alternate DTO shape does not silently fail to match.
 *
 * Pure: no React, no Office.js, no network. The network half (list the
 * caller's instances, call this matcher, swallow failures to `null`) stays
 * host-side — the add-in's `findRunningInstanceForEmail.ts::lookup` and
 * ora's `find-blocking` action intent / `oraLaunchIO.findBlockingInstance`.
 */

/**
 * Statuses that drive the "Active Workflows" list / "should this run take
 * over the pane" question. Excludes the two PARKED non-terminal statuses
 * (`pending_email_send`, `pending_edocs`) — neither is actionable from that
 * list; see `LAUNCH_BLOCKING_STATUSES` for why they DO count for the
 * concurrent-run question below.
 */
export const IN_PROGRESS_STATUSES = ["running", "paused_hitl", "warm_hitl"] as const;

/**
 * Every NON-TERMINAL status — the set the substrate's same-email
 * concurrent-run guard blocks a second launch on (`start_workflow` step 6b,
 * mirroring `workflows.types.status.NON_TERMINAL_STATUSES`).
 *
 * Deliberately WIDER than `IN_PROGRESS_STATUSES`, and the two must not be
 * merged — they answer different questions:
 *
 *   - `IN_PROGRESS_STATUSES` — "should this run take over the pane / show in
 *     the active list?" Parked runs must NOT, because they need nothing from
 *     the AM.
 *   - `LAUNCH_BLOCKING_STATUSES` — "would the substrate refuse a second
 *     launch for this email?" Parked runs DO block: they have already
 *     written the PCHG, so a second run duplicates it just like a live run
 *     would.
 */
export const LAUNCH_BLOCKING_STATUSES = [
  "running",
  "paused_hitl",
  "warm_hitl",
  "pending_email_send",
  "pending_edocs",
] as const;

/**
 * The minimal instance-row shape this module needs — satisfied structurally
 * by ora's `SubstrateInstanceItem` and the add-in's `ActiveWorkflow` without
 * either host needing to import the other's type.
 */
export interface BlockingInstanceRow {
  /** Both real DTOs declare this. */
  instance_id?: string | null;
  /** Accepted defensively — see the module docstring. */
  id?: string | null;
  status: string;
  metadata?: Record<string, unknown> | null;
}

/** `row.instance_id`, falling back to `row.id` — never both, `instance_id`
 *  wins when both are present. */
function rowInstanceId(row: BlockingInstanceRow): string {
  if (typeof row.instance_id === "string" && row.instance_id) return row.instance_id;
  if (typeof row.id === "string" && row.id) return row.id;
  return "";
}

/**
 * The email-id surface this lookup needs (structural).
 *
 * Each id is independently optional AND nullable: callers thread these
 * straight from props/context where "not captured yet" is `null` on some
 * paths and `undefined` on others. `candidateIds` filters to non-empty
 * strings anyway, so accepting null here keeps call sites free of coercion.
 */
export interface EmailIdSource {
  emailId?: string | null;
  internetMessageId?: string | null;
  restId?: string | null;
}

/**
 * The email's ids, most authoritative FIRST: the RFC 5322 Message-ID is the
 * per-email identity every producer now declares, so the guess across the
 * mailbox-scoped id shapes is the fallback rather than the primary path.
 */
export function candidateIds(email: EmailIdSource): string[] {
  return [email.internetMessageId, email.restId, email.emailId].filter(
    (v): v is string => typeof v === "string" && v.length > 0,
  );
}

/**
 * Matching anchors (instance metadata, written at launch admit and
 * preserved by `emit_push`'s metadata MERGE), in preference order:
 *   - `metadata.internet_message_id`        — THE DECLARED ANCHOR. The
 *     per-email identity `start_workflow` stamps at admit from the declared
 *     `internet_message_id` input. Prefer it: it is the one value the guard,
 *     the extraction cache and this lookup all agree on.
 *   - `metadata.graph_message_id`           — LEGACY. The Office item id the
 *     add-in threads into inputs (merged into metadata by `start_workflow`).
 *   - `metadata.email.internet_message_id`  — LEGACY. The RFC 5322
 *     Message-ID the add-in mirrors for the launch-feedback flywheel.
 *
 * The two legacy anchors STAY for the rollout window: instances started
 * before the declared field existed carry no `metadata.internet_message_id`,
 * and dropping them would strand a live run's lookup.
 */
export function instanceEmailAnchors(row: BlockingInstanceRow): string[] {
  const meta = row.metadata;
  if (!meta || typeof meta !== "object") return [];
  const metaRecord = meta as Record<string, unknown>;
  const anchors: string[] = [];
  const declared = metaRecord.internet_message_id;
  if (typeof declared === "string" && declared) anchors.push(declared);
  const gmid = metaRecord.graph_message_id;
  if (typeof gmid === "string" && gmid) anchors.push(gmid);
  const email = metaRecord.email;
  if (email && typeof email === "object") {
    const imid = (email as Record<string, unknown>).internet_message_id;
    if (typeof imid === "string" && imid) anchors.push(imid);
  }
  return anchors;
}

/**
 * Pure matcher — exported for tests. Returns the most-recently-updated
 * instance in `statuses` whose metadata anchors intersect the email's ids
 * (rows are assumed already ordered `updated_at` DESC by the caller's list
 * endpoint, exactly like the add-in's copy assumes).
 *
 * `statuses` defaults to `IN_PROGRESS_STATUSES` (the reopen-the-pane
 * question). Pass `LAUNCH_BLOCKING_STATUSES` to ask the wider "would a
 * second launch be refused?" question instead — see that constant for why
 * the two differ.
 */
export function matchRunningInstance<T extends BlockingInstanceRow>(
  rows: readonly T[],
  email: EmailIdSource,
  statuses: readonly string[] = IN_PROGRESS_STATUSES,
): T | null {
  const ids = new Set(candidateIds(email));
  if (ids.size === 0) return null;
  for (const row of rows) {
    // The list endpoint is already status-filtered + updated_at DESC, but
    // re-check status defensively so a stale row can't hijack the result.
    if (!statuses.includes(row.status)) continue;
    if (instanceEmailAnchors(row).some((a) => ids.has(a))) return row;
  }
  return null;
}

export { rowInstanceId };
